import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import { API, fmtErr } from "../lib/api";
import { normalizeSiteContent, parseLegalContent } from "../lib/siteContent";

export const DOCS = {
  terms: {
    title: "Terms & Conditions",
    updated: "Last updated: October 2026",
    testid: "legal-terms",
    sections: [
      ["About Comfile's services", [
        "Comfile provides accounting, tax, registration and business-compliance support. The exact work available for a request is confirmed separately based on your requirements and applicable rules.",
        "By using this website or requesting a service, you agree to these Terms & Conditions and our Privacy Policy.",
      ]],
      ["Service scope and responsibilities", [
        "Before work begins, Comfile will confirm the agreed scope, information or documents required, applicable fees and expected next steps. A service engagement begins only after both sides accept the applicable proposal or service request.",
        "You are responsible for providing complete, accurate and timely information, reviewing drafts and confirmations, and meeting your own statutory obligations and deadlines.",
        "A filing or application is subject to review and processing by the relevant government authority, bank, registry or other third party. Comfile cannot guarantee approval, a particular outcome or a processing time outside its control.",
      ]],
      ["Fees and payment", [
        "Fees, taxes, payment milestones and any third-party charges are confirmed for the specific service before payment. Payments for an approved request or invoice are made through the client portal or another method confirmed by Comfile.",
        "Do not make a payment against an unconfirmed service or fee. The applicable engagement terms govern cancellations, refunds and work already performed.",
      ]],
      ["Use of this website", [
        "Website information is general in nature and may change. You must not misuse the website, submit unlawful material or attempt to disrupt its operation.",
        "These terms do not transfer ownership of Comfile's website content, branding or materials to you.",
      ]],
      ["Changes and questions", [
        "These terms may be updated by publishing a revised version on this page. The terms applicable to a particular engagement may also be set out in its accepted proposal or service request.",
        "For questions about a service request, use the contact details or client portal provided by Comfile.",
      ]],
    ],
  },
  privacy: {
    title: "Privacy Policy",
    updated: "Last updated: October 2026",
    testid: "legal-privacy",
    sections: [
      ["Information we receive", [
        "When you contact us or request a service, we may receive your name, contact details, business information, service requirements and messages.",
        "If you engage Comfile, we may receive accounting records, identity or registration documents, tax information and other materials needed for the agreed work.",
        "Our website or service providers may collect basic technical information needed to operate, secure and troubleshoot the website or client portal.",
      ]],
      ["How we use information", [
        "We use information to respond to enquiries, assess and deliver requested accounting, tax, registration or compliance services, communicate about your request, and administer payments and records.",
        "We do not sell personal or business information. We use it only for the purposes described here, purposes you authorize, or as otherwise permitted or required by law.",
      ]],
      ["Sharing and service providers", [
        "Information may be shared with the relevant government authority, bank, registry, payment provider or professional adviser when needed to carry out your requested service, with your authorization, or when required by law.",
        "We may use service providers to host, operate or support our website and client portal. Those providers process information for their service purposes and under their applicable terms.",
      ]],
      ["Security and retention", [
        "We take reasonable steps to handle information carefully and limit access to people who need it for the relevant service. No method of transmission or storage can be guaranteed completely secure.",
        "We retain records for as long as needed to respond to your request, provide the agreed service, meet legal or professional recordkeeping obligations, resolve disputes and protect our legitimate interests.",
      ]],
      ["Your choices and requests", [
        "Please avoid sending sensitive personal or financial documents through a general enquiry form. If documents are needed for an engagement, use the channel specified by Comfile.",
        "You may contact Comfile to request access to or correction of information you have provided, subject to applicable law and recordkeeping requirements.",
      ]],
      ["Policy updates", [
        "We may update this policy by publishing a revised version on this page and changing the date above.",
      ]],
    ],
  },
  refund: {
    title: "Refund & Cancellation Policy",
    updated: "Last updated: October 2026",
    testid: "legal-refund",
    sections: [
      ["Service fees", [
        "Service scope, fees, applicable taxes and payment milestones are confirmed before a client makes payment. Fees may vary depending on the facts, documents and work required.",
        "Once work has started, fees for work already performed and third-party or statutory charges already incurred are generally not refundable, subject to the accepted engagement terms and applicable law.",
      ]],
      ["Cancellation and refund requests", [
        "If you wish to cancel a service, contact Comfile as soon as possible. Any refund for work not yet performed will be assessed under the accepted engagement terms and applicable law.",
        "Duplicate payments or payments for a failed transaction that was not credited to the relevant service request will be reviewed for correction or refund.",
      ]],
      ["How to contact us", [
        "Submit a request through the client portal or contact the Comfile team using the details provided with your service request. Include the relevant invoice or payment reference and a brief explanation.",
        "Approved refunds are returned using the original payment method where possible. Processing time may depend on the payment provider.",
      ]],
    ],
  },
  confidentiality: {
    title: "Confidentiality Policy",
    updated: "Last updated: October 2026",
    testid: "legal-confidentiality",
    sections: [
      ["Handling confidential information", [
        "Information and documents you provide for an enquiry or accepted service engagement are handled for the purpose of assessing, delivering and administering that work.",
        "We take reasonable steps to limit access to information to team members and service providers who need it for the relevant purpose.",
      ]],
      ["Permitted disclosures", [
        "Information may be shared with a government authority, bank, registry, payment provider or professional adviser when needed to carry out work you requested or authorized.",
        "We may disclose information when required by law or a valid legal process. Information that is public, independently obtained or lawfully received without confidentiality restrictions is not treated as confidential under this policy.",
      ]],
      ["Your responsibility", [
        "Please use the secure channel specified by Comfile for client documents and avoid including sensitive records in a general website enquiry.",
        "Confidentiality is subject to applicable law, professional obligations and the terms agreed for a particular engagement.",
      ]],
    ],
  },
  disclaimer: {
    title: "Disclaimer Policy",
    updated: "Last updated: October 2026",
    testid: "legal-disclaimer",
    sections: [
      ["General information", [
        "Information on this website is provided for general information about Comfile's accounting, tax, registration and business-compliance services. It is not a substitute for advice based on your specific facts and current law.",
      ]],
      ["Service scope and outcomes", [
        "The applicability of a service, required documents, fees and timelines depend on the facts of each case and are confirmed after review.",
        "Applications and filings may be reviewed by government authorities or other third parties. Comfile does not guarantee acceptance, approval or a particular processing time.",
        "A professional engagement is formed only when the scope and applicable terms have been agreed. Website content alone does not create an adviser-client relationship.",
      ]],
      ["External links and changes", [
        "Links to external websites are provided for convenience. Comfile does not control or endorse their content and is not responsible for their availability or accuracy.",
        "Website content may be updated without notice. Please contact Comfile to discuss requirements that affect a specific service request.",
      ]],
    ],
  },
};

export default function Legal({ doc }) {
  const d = DOCS[doc] || DOCS.terms;
  const [customDoc, setCustomDoc] = useState(null);

  useEffect(() => {
    setCustomDoc(null);
    API.get("/public/site-content")
      .then(({ data }) => setCustomDoc(normalizeSiteContent(data, DOCS).legal[doc]))
      .catch((error) => toast.error(fmtErr(error)));
  }, [doc]);

  const title = customDoc?.title || d.title;
  const updated = customDoc?.updated || d.updated;
  const customSections = typeof customDoc?.content === "string"
    ? parseLegalContent(customDoc.content)
    : null;

  return (
    <div className="min-h-screen bg-background" data-testid="legal-page">
      <header className="border-b">
        <div className="mx-auto flex h-16 max-w-3xl items-center justify-between px-4 sm:px-6">
          <Link to="/" className="flex items-center" aria-label="Comfile home">
            <img src="/comfile-logo.svg" alt="Comfile" className="h-12 w-40 object-contain object-left" />
          </Link>
          <Link to="/" className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline" data-testid="legal-back-link">
            <ArrowLeft size={15} /> Back to home
          </Link>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-4 py-12 sm:px-6" data-testid={d.testid}>
        <h1 className="font-heading text-3xl font-bold tracking-tight sm:text-4xl">{title}</h1>
        <p className="mt-2 text-xs text-muted-foreground">{updated}</p>
        <div className="mt-8 space-y-8">
          {(customSections || d.sections.map(([heading, paragraphs]) => ({ heading, paragraphs }))).map(({ heading, paragraphs }) => (
            <section key={heading}>
              <h2 className="font-heading text-lg font-semibold tracking-tight">{heading}</h2>
              {customSections ? (
                <div className="mt-2 space-y-2 text-sm leading-relaxed text-muted-foreground">
                  {paragraphs.map((paragraph, index) => <p key={`${heading}-${index}`}>{paragraph}</p>)}
                </div>
              ) : (
                <ul className="mt-2 list-disc space-y-1.5 pl-5 text-sm leading-relaxed text-muted-foreground">
                  {paragraphs.map((paragraph, index) => <li key={`${heading}-${index}`}>{paragraph}</li>)}
                </ul>
              )}
            </section>
          ))}
        </div>
        <div className="mt-12 flex flex-wrap gap-4 border-t pt-6 text-sm" data-testid="legal-cross-links">
          <Link to="/terms" className="text-primary hover:underline">Terms &amp; Conditions</Link>
          <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>
          <Link to="/refund" className="text-primary hover:underline">Refund Policy</Link>
          <Link to="/confidentiality" className="text-primary hover:underline">Confidentiality Policy</Link>
          <Link to="/disclaimer" className="text-primary hover:underline">Disclaimer Policy</Link>
        </div>
      </main>
    </div>
  );
}
